/**
 * Access token bridge between the WorkOS AuthKit client and Apollo.
 *
 * Apollo clients are constructed at module scope, outside React, so they
 * cannot call `useAuth()` to reach the AuthKit client. AuthProvider registers
 * the SDK's token getter here on mount; the Apollo auth link reads through
 * `getAccessTokenForRequest()` on every operation.
 *
 * Fetching per request is what keeps sessions alive: WorkOS access tokens are
 * short-lived and the SDK rotates them on a timer. A token cached at login
 * goes stale within minutes, and since the API now verifies `exp`, every
 * request after that point fails and the user is bounced to /login.
 */

/** Where the token is mirrored for non-Apollo readers (e.g. the WebSocket). */
export const ACCESS_TOKEN_STORAGE_KEY = 'token';

type AccessTokenGetter = () => Promise<string | null>;

let getter: AccessTokenGetter | null = null;

/**
 * Register the AuthKit token getter. Called by AuthProvider; passing null on
 * unmount/sign-out clears it so no stale closure survives a logout.
 */
export function registerAccessTokenGetter(fn: AccessTokenGetter | null): void {
  getter = fn;
}

/**
 * The token to send with the next request.
 *
 * Asks the SDK first - it returns its cached token and refreshes when the
 * token is close to expiry. Falls back to the mirrored copy only before the
 * provider has mounted, so requests made during boot still carry auth.
 */
export async function getAccessTokenForRequest(): Promise<string | null> {
  if (getter) {
    try {
      const token = await getter();
      if (token) {
        // Mirror for readers that cannot await (the realtime WebSocket builds
        // its URL synchronously).
        localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
        return token;
      }
      // No token means no active session - drop the stale mirror rather than
      // letting another reader authenticate with it.
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      return null;
    } catch (error) {
      console.warn('[Auth] Failed to get access token from AuthKit', error);
      // Fall through to the mirrored copy: a transient refresh failure
      // shouldn't drop auth off a request the SDK may still consider valid.
    }
  }

  return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}
